import React from 'react';
import Icon from '../../../components/AppIcon';
import { getTranslation } from '../../../utils/translations';

const GeographicAccessSection = ({ roleAssignments, language }) => {
  const t = getTranslation(language);

  // Group assignments by country
  const assignmentsByCountry = roleAssignments?.reduce((acc, assignment) => {
    const countryName = assignment?.country?.countryName || 'Global';
    if (!acc?.[countryName]) {
      acc[countryName] = {
        country: assignment?.country,
        assignments: []
      };
    }
    acc?.[countryName]?.assignments?.push(assignment);
    return acc;
  }, {});

  const getRoleColor = (roleType) => {
    const colors = {
      dentist_client: 'bg-blue-100 text-blue-700',
      dentist_supplier: 'bg-green-100 text-green-700',
      distributor: 'bg-purple-100 text-purple-700',
      administrator: 'bg-red-100 text-red-700',
      production_supplier: 'bg-orange-100 text-orange-700',
      production_responsible: 'bg-amber-100 text-amber-800',
      logistics_coordinator: 'bg-teal-100 text-teal-800'
    };
    return colors?.[roleType] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-foreground">
        {language === 'FR' ? 'Accès Géographique' : 'Geographic Access'}
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(assignmentsByCountry)?.map(([countryName, data]) => (
          <div
            key={countryName}
            className="bg-card border border-border rounded-lg p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon name="MapPin" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">{countryName}</h3>
                {data?.country && (
                  <p className="text-sm text-muted-foreground">
                    {data?.country?.countryCode} • {data?.country?.region}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-2">
                {data?.assignments?.length} {language === 'FR' ? 'attribution(s)' : 'assignment(s)'}
              </p>
              
              <div className="space-y-2">
                {data?.assignments?.slice(0, 5)?.map((assignment) => (
                  <div
                    key={assignment?.id}
                    className="flex items-center justify-between p-2 rounded-lg bg-muted/30"
                  >
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(assignment?.role?.roleType)}`}>
                      {assignment?.role?.roleName}
                    </span>
                    {assignment?.regionAccess?.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Icon name="MapPin" size={14} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {assignment?.regionAccess?.length} {language === 'FR' ? 'région(s)' : 'region(s)'}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
                {data?.assignments?.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    +{data?.assignments?.length - 5} {language === 'FR' ? 'de plus' : 'more'}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {roleAssignments?.length === 0 && (
        <div className="text-center py-12 bg-muted/30 rounded-lg border border-border">
          <Icon name="Globe" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t?.noData}</p>
        </div>
      )}
    </div>
  );
};

export default GeographicAccessSection;